import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Plug, 
  Facebook, 
  FileSpreadsheet, 
  Globe, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  Copy, 
  ExternalLink,
  RefreshCw,
  Settings,
  Trash2,
  Plus,
  Code,
  AlertCircle,
  Info,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Separate component to render sheets list (avoids babel plugin issue)
const SheetsList = ({ sheets }) => {
  const items = [];
  const displaySheets = sheets.slice(0, 2);
  for (let i = 0; i < displaySheets.length; i++) {
    const sheet = displaySheets[i];
    items.push(
      <div key={sheet.id} className="text-sm text-muted-foreground flex items-center justify-between">
        <span className="truncate">{sheet.name}</span>
        <Badge variant="outline" className="text-xs">
          {sheet.leads_synced || 0} leads
        </Badge>
      </div>
    );
  }
  return <>{items}</>;
};

// Separate component for sheets table rows
const SheetsTableRows = ({ sheets, onSync, onEdit, onDelete }) => {
  const rows = [];
  for (let i = 0; i < sheets.length; i++) {
    const sheet = sheets[i];
    rows.push(
      <TableRow key={sheet.id}>
        <TableCell className="font-medium">{sheet.name}</TableCell>
        <TableCell className="font-mono text-xs truncate max-w-[150px]">{sheet.spreadsheet_id}</TableCell>
        <TableCell>{sheet.sync_interval} min</TableCell>
        <TableCell>
          {sheet.last_sync ? new Date(sheet.last_sync).toLocaleString() : 'Never'}
        </TableCell>
        <TableCell>
          <Badge variant="secondary">{sheet.leads_synced || 0}</Badge>
        </TableCell>
        <TableCell>
          {sheet.is_active ? (
            <Badge className="bg-green-500/10 text-green-600">Active</Badge>
          ) : (
            <Badge variant="outline">Paused</Badge>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onSync(sheet.id)} title="Sync Now">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onEdit(sheet)} title="Edit">
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(sheet.id)} title="Delete">
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }
  return <>{rows}</>;
};

// Column mapping fields component
const ColumnMappingFields = ({ mapping, onChange }) => {
  const fields = [];
  const entries = Object.entries(mapping);
  for (let i = 0; i < entries.length; i++) {
    const [field, column] = entries[i];
    fields.push(
      <div key={field} className="flex items-center gap-2">
        <Label className="w-24 text-xs capitalize">{field.replace('_', ' ')}</Label>
        <Input
          value={column}
          onChange={(e) => onChange(field, e.target.value.toUpperCase())}
          placeholder="A"
          className="w-16 text-center uppercase"
        />
      </div>
    );
  }
  return <>{fields}</>;
};

// Forms select options component
const FormsSelectOptions = ({ forms }) => {
  const options = [];
  for (let i = 0; i < forms.length; i++) {
    const form = forms[i];
    options.push(
      <SelectItem key={form.id} value={form.id}>{form.name}</SelectItem>
    );
  }
  return <>{options}</>;
};

const IntegrationsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState({
    facebook: null,
    google_sheets: [],
    website_embeds: []
  });
  const [forms, setForms] = useState([]);
  
  // Dialog states
  const [fbDialogOpen, setFbDialogOpen] = useState(false);
  const [sheetsDialogOpen, setSheetsDialogOpen] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  
  // Facebook form
  const [fbForm, setFbForm] = useState({
    app_id: '',
    app_secret: '',
    page_id: '',
    page_access_token: ''
  });
  const [savingFb, setSavingFb] = useState(false);
  
  // Google Sheets form
  const [sheetsForm, setSheetsForm] = useState({
    name: '',
    spreadsheet_id: '',
    sheet_name: 'Sheet1',
    credentials_json: '',
    column_mapping: {
      first_name: 'A',
      last_name: 'B',
      email: 'C',
      phone: 'D',
      source: 'E'
    },
    sync_interval: 5,
    start_row: 2
  });
  const [savingSheets, setSavingSheets] = useState(false);
  const [testingSheets, setTestingSheets] = useState(false);
  
  useEffect(() => {
    fetchIntegrations();
    fetchForms();
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await api().get('/integrations');
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      const response = await api().get('/forms');
      // Filter for published forms (status === 'published')
      setForms(response.data.forms?.filter(f => f.status === 'published') || []);
    } catch (error) {
      console.error('Failed to load forms:', error);
    }
  };

  // Facebook Integration
  const openFbDialog = () => {
    if (integrations.facebook) {
      setFbForm({
        app_id: integrations.facebook.app_id || '',
        app_secret: '',
        page_id: integrations.facebook.page_id || '',
        page_access_token: ''
      });
    } else {
      setFbForm({
        app_id: '',
        app_secret: '',
        page_id: '',
        page_access_token: ''
      });
    }
    setFbDialogOpen(true);
  };

  const saveFacebookIntegration = async () => {
    if (!fbForm.app_id || !fbForm.app_secret) {
      toast.error('Please provide App ID and App Secret');
      return;
    }
    
    setSavingFb(true);
    try {
      await api().post('/integrations/facebook', fbForm);
      toast.success('Facebook integration saved!');
      setFbDialogOpen(false);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Facebook integration');
    } finally {
      setSavingFb(false);
    }
  };

  const disconnectFacebook = async () => {
    if (!confirm('Are you sure you want to disconnect Facebook?')) return;
    
    try {
      await api().delete('/integrations/facebook');
      setIntegrations(prev => ({ ...prev, facebook: null }));
      toast.success('Facebook disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Facebook');
    }
  };

  // Google Sheets Integration
  const openSheetsDialog = (existing = null) => {
    if (existing) {
      setSheetsForm({
        id: existing.id,
        name: existing.name,
        spreadsheet_id: existing.spreadsheet_id,
        sheet_name: existing.sheet_name || 'Sheet1',
        credentials_json: '',
        column_mapping: existing.column_mapping || {
          first_name: 'A',
          last_name: 'B',
          email: 'C',
          phone: 'D',
          source: 'E'
        },
        sync_interval: existing.sync_interval || 5,
        start_row: existing.start_row || 2
      });
    } else {
      setSheetsForm({
        name: '',
        spreadsheet_id: '',
        sheet_name: 'Sheet1',
        credentials_json: '',
        column_mapping: {
          first_name: 'A',
          last_name: 'B',
          email: 'C',
          phone: 'D',
          source: 'E'
        },
        sync_interval: 5,
        start_row: 2
      });
    }
    setSheetsDialogOpen(true);
  };

  const testGoogleSheets = async () => {
    if (!sheetsForm.spreadsheet_id || !sheetsForm.credentials_json) {
      toast.error('Please provide Spreadsheet ID and credentials');
      return;
    }
    
    setTestingSheets(true);
    try {
      const response = await api().post('/integrations/google-sheets/test', {
        spreadsheet_id: sheetsForm.spreadsheet_id,
        sheet_name: sheetsForm.sheet_name,
        credentials_json: sheetsForm.credentials_json
      });
      
      if (response.data.success) {
        toast.success(`Connected! Found ${response.data.row_count} rows in "${response.data.sheet_name}"`);
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test connection');
    } finally {
      setTestingSheets(false);
    }
  };

  const saveGoogleSheets = async () => {
    if (!sheetsForm.name || !sheetsForm.spreadsheet_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    if (!sheetsForm.id && !sheetsForm.credentials_json) {
      toast.error('Please provide Google API credentials');
      return;
    }
    
    setSavingSheets(true);
    try {
      const payload = {
        name: sheetsForm.name,
        spreadsheet_id: sheetsForm.spreadsheet_id,
        sheet_name: sheetsForm.sheet_name,
        column_mapping: sheetsForm.column_mapping,
        sync_interval: sheetsForm.sync_interval,
        start_row: sheetsForm.start_row
      };
      
      if (sheetsForm.credentials_json) {
        payload.credentials_json = sheetsForm.credentials_json;
      }
      
      if (sheetsForm.id) {
        await api().put(`/integrations/google-sheets/${sheetsForm.id}`, payload);
        toast.success('Integration updated');
      } else {
        await api().post('/integrations/google-sheets', payload);
        toast.success('Google Sheets connected!');
      }
      
      setSheetsDialogOpen(false);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save integration');
    } finally {
      setSavingSheets(false);
    }
  };

  const syncGoogleSheets = async (integrationId) => {
    try {
      const response = await api().post(`/integrations/google-sheets/${integrationId}/sync`);
      toast.success(`Synced ${response.data.new_leads} new leads`);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    }
  };

  const deleteGoogleSheets = async (integrationId) => {
    if (!confirm('Are you sure you want to delete this integration?')) return;
    
    try {
      await api().delete(`/integrations/google-sheets/${integrationId}`);
      toast.success('Integration deleted');
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleColumnMappingChange = (field, value) => {
    setSheetsForm({
      ...sheetsForm,
      column_mapping: { ...sheetsForm.column_mapping, [field]: value }
    });
  };

  // Website Embed
  const getEmbedCode = (form, type = 'iframe') => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
    const slug = form.unique_slug || form.slug;
    const formUrl = `${baseUrl}/public/forms/${slug}`;
    
    if (type === 'iframe') {
      return `<!-- EduBridge Network Lead Form Embed -->
<iframe 
  src="${formUrl}?embed=true" 
  width="100%" 
  height="600" 
  frameborder="0" 
  style="border: none; max-width: 600px;"
  title="${form.name}"
></iframe>`;
    } else {
      return `<!-- EduBridge Network Lead Form Script -->
<div id="unicrm-form-${slug}"></div>
<script>
(function() {
  var container = document.getElementById('unicrm-form-${slug}');
  var iframe = document.createElement('iframe');
  iframe.src = '${formUrl}?embed=true';
  iframe.width = '100%';
  iframe.height = '600';
  iframe.frameBorder = '0';
  iframe.style.border = 'none';
  iframe.style.maxWidth = '600px';
  container.appendChild(iframe);
})();
</script>`;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const googleSheetsCount = integrations.google_sheets?.length || 0;
  const formsCount = forms.length;
  const facebookConnected = integrations.facebook && integrations.facebook.is_active;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Lead Integrations</h1>
        <p className="text-muted-foreground">Connect your lead sources to automatically capture leads from multiple channels</p>
      </div>

      {/* Integration Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Facebook Lead Ads Card */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16" />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Facebook className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Facebook Lead Ads</CardTitle>
                <CardDescription>Capture leads from Facebook & Instagram ads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {facebookConnected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">Connected</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  App ID: {integrations.facebook.app_id?.slice(0, 8)}...
                </p>
                <p className="text-sm text-muted-foreground">
                  Leads synced: {integrations.facebook.leads_synced || 0}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Not connected</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Connect your Facebook Business account to receive leads in real-time via webhooks
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            {facebookConnected ? (
              <div className="flex gap-2 w-full">
                <Button variant="outline" className="flex-1" onClick={() => setFbDialogOpen(true)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
                <Button variant="outline" size="icon" onClick={disconnectFacebook}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button className="w-full" onClick={() => setFbDialogOpen(true)} data-testid="connect-facebook-btn">
                <Facebook className="w-4 h-4 mr-2" />
                Connect Facebook
              </Button>
            )}
          </CardFooter>
        </Card>

        {/* Google Sheets Card */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full -mr-16 -mt-16" />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <FileSpreadsheet className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Google Sheets</CardTitle>
                <CardDescription>Import leads from spreadsheets automatically</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {googleSheetsCount > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">{googleSheetsCount} sheet(s) connected</span>
                </div>
                <SheetsList sheets={integrations.google_sheets} />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">No sheets connected</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Sync leads from Google Sheets automatically every few minutes
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button className="w-full" onClick={() => openSheetsDialog()} data-testid="connect-sheets-btn">
              <Plus className="w-4 h-4 mr-2" />
              {googleSheetsCount > 0 ? 'Add Another Sheet' : 'Connect Google Sheets'}
            </Button>
          </CardFooter>
        </Card>

        {/* Website Embed Card */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -mr-16 -mt-16" />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Globe className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Website Forms</CardTitle>
                <CardDescription>Embed lead forms on your website</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formsCount > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">{formsCount} form(s) available</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get embed code for your published forms to use on WordPress or any website
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-600">No published forms</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Create and publish a form in Form Creator to get embed code
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              variant={formsCount > 0 ? "default" : "outline"}
              onClick={() => formsCount > 0 ? setEmbedDialogOpen(true) : window.location.href = '/form-creator'}
              data-testid="get-embed-btn"
            >
              <Code className="w-4 h-4 mr-2" />
              {formsCount > 0 ? 'Get Embed Code' : 'Create Form First'}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Connected Google Sheets List */}
      {googleSheetsCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Connected Google Sheets
            </CardTitle>
            <CardDescription>Manage your Google Sheets integrations</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Spreadsheet ID</TableHead>
                  <TableHead>Sync Interval</TableHead>
                  <TableHead>Last Sync</TableHead>
                  <TableHead>Leads Synced</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SheetsTableRows 
                  sheets={integrations.google_sheets} 
                  onSync={syncGoogleSheets}
                  onEdit={openSheetsDialog}
                  onDelete={deleteGoogleSheets}
                />
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Google Sheets Dialog */}
      <Dialog open={sheetsDialogOpen} onOpenChange={setSheetsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              {sheetsForm.id ? 'Edit Google Sheets Integration' : 'Connect Google Sheets'}
            </DialogTitle>
            <DialogDescription>
              Automatically sync leads from a Google Spreadsheet
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Setup Instructions */}
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">Setup Instructions:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700 dark:text-blue-300">
                    <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                    <li>Create a project and enable Google Sheets API</li>
                    <li>Create a Service Account and download the JSON key</li>
                    <li>Share your spreadsheet with the service account email</li>
                    <li>Paste the JSON credentials below</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Integration Name *</Label>
                <Input
                  value={sheetsForm.name}
                  onChange={(e) => setSheetsForm({ ...sheetsForm, name: e.target.value })}
                  placeholder="e.g., Facebook Leads Sheet"
                  data-testid="sheets-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Spreadsheet ID *</Label>
                <Input
                  value={sheetsForm.spreadsheet_id}
                  onChange={(e) => setSheetsForm({ ...sheetsForm, spreadsheet_id: e.target.value })}
                  placeholder="From URL: docs.google.com/spreadsheets/d/{SPREADSHEET_ID}/..."
                  data-testid="sheets-id-input"
                />
                <p className="text-xs text-muted-foreground">
                  Find this in your Google Sheets URL between /d/ and /edit
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sheet Name</Label>
                  <Input
                    value={sheetsForm.sheet_name}
                    onChange={(e) => setSheetsForm({ ...sheetsForm, sheet_name: e.target.value })}
                    placeholder="Sheet1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Row</Label>
                  <Input
                    type="number"
                    min="1"
                    value={sheetsForm.start_row}
                    onChange={(e) => setSheetsForm({ ...sheetsForm, start_row: parseInt(e.target.value) || 2 })}
                    placeholder="2 (skip header)"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Google API Credentials (JSON) {sheetsForm.id ? '(leave empty to keep existing)' : '*'}</Label>
                <Textarea
                  value={sheetsForm.credentials_json}
                  onChange={(e) => setSheetsForm({ ...sheetsForm, credentials_json: e.target.value })}
                  placeholder='Paste your service account JSON key here...'
                  rows={4}
                  className="font-mono text-xs"
                  data-testid="sheets-credentials-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Column Mapping</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Map spreadsheet columns (A, B, C...) to lead fields
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <ColumnMappingFields 
                    mapping={sheetsForm.column_mapping} 
                    onChange={handleColumnMappingChange} 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sync Interval (minutes)</Label>
                <Select
                  value={String(sheetsForm.sync_interval)}
                  onValueChange={(v) => setSheetsForm({ ...sheetsForm, sync_interval: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every 1 minute</SelectItem>
                    <SelectItem value="5">Every 5 minutes</SelectItem>
                    <SelectItem value="15">Every 15 minutes</SelectItem>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={testGoogleSheets} disabled={testingSheets}>
              {testingSheets ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
            <Button onClick={saveGoogleSheets} disabled={savingSheets} data-testid="save-sheets-btn">
              {savingSheets ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {sheetsForm.id ? 'Update' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Website Embed Dialog */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-amber-500" />
              Website Embed Code
            </DialogTitle>
            <DialogDescription>
              Copy the code below and paste it into your website
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Form Selection */}
            <div className="space-y-2">
              <Label>Select Form</Label>
              <Select
                value={selectedForm?.id || ''}
                onValueChange={(v) => setSelectedForm(forms.find(f => f.id === v))}
              >
                <SelectTrigger data-testid="embed-form-select">
                  <SelectValue placeholder="Choose a published form" />
                </SelectTrigger>
                <SelectContent>
                  <FormsSelectOptions forms={forms} />
                </SelectContent>
              </Select>
            </div>

            {selectedForm && (
              <Tabs defaultValue="iframe">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="iframe">IFrame Embed</TabsTrigger>
                  <TabsTrigger value="script">JavaScript Embed</TabsTrigger>
                </TabsList>
                
                <TabsContent value="iframe" className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Simple iframe embed - just paste this HTML where you want the form to appear
                  </p>
                  <div className="relative">
                    <pre className="p-4 rounded-lg bg-muted text-xs overflow-x-auto">
                      {getEmbedCode(selectedForm, 'iframe')}
                    </pre>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(getEmbedCode(selectedForm, 'iframe'))}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="script" className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    JavaScript embed - provides more flexibility for styling
                  </p>
                  <div className="relative">
                    <pre className="p-4 rounded-lg bg-muted text-xs overflow-x-auto">
                      {getEmbedCode(selectedForm, 'script')}
                    </pre>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(getEmbedCode(selectedForm, 'script'))}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            )}

            {selectedForm && (
              <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Leads submitted through this form will appear in your Leads page with source "Web Form"</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            {selectedForm && (
              <Button variant="outline" onClick={() => window.open(`/public/forms/${selectedForm.unique_slug || selectedForm.slug}`, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Preview Form
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Facebook Integration Dialog */}
      <Dialog open={fbDialogOpen} onOpenChange={setFbDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Facebook className="w-5 h-5 text-blue-600" />
              {integrations.facebook ? 'Update Facebook Integration' : 'Connect Facebook Lead Ads'}
            </DialogTitle>
            <DialogDescription>
              Receive leads from your Facebook and Instagram ad campaigns in real-time
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Setup Instructions */}
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">Setup Instructions:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700 dark:text-blue-300">
                    <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">Facebook Developers</a></li>
                    <li>Create or select an app with "Marketing API" product</li>
                    <li>Copy your App ID and App Secret from Settings → Basic</li>
                    <li>Add Webhooks product and subscribe to "leadgen" events</li>
                    <li>Get a Page Access Token with "leads_retrieval" permission</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Facebook App ID *</Label>
                <Input
                  value={fbForm.app_id}
                  onChange={(e) => setFbForm({ ...fbForm, app_id: e.target.value })}
                  placeholder="Your Facebook App ID"
                  data-testid="fb-app-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label>App Secret *</Label>
                <Input
                  type="password"
                  value={fbForm.app_secret}
                  onChange={(e) => setFbForm({ ...fbForm, app_secret: e.target.value })}
                  placeholder={integrations.facebook ? "Leave empty to keep existing" : "Your Facebook App Secret"}
                  data-testid="fb-app-secret-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Page ID</Label>
                <Input
                  value={fbForm.page_id}
                  onChange={(e) => setFbForm({ ...fbForm, page_id: e.target.value })}
                  placeholder="Your Facebook Page ID"
                />
                <p className="text-xs text-muted-foreground">
                  Find your Page ID in Page Settings → Page Info
                </p>
              </div>

              <div className="space-y-2">
                <Label>Page Access Token</Label>
                <Textarea
                  value={fbForm.page_access_token}
                  onChange={(e) => setFbForm({ ...fbForm, page_access_token: e.target.value })}
                  placeholder={integrations.facebook?.page_access_token ? "***configured*** - leave empty to keep" : "Long-lived Page Access Token"}
                  rows={3}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Generate from Graph API Explorer with leads_retrieval permission
                </p>
              </div>
            </div>

            {/* Webhook URL Info */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200 mb-2">Webhook Configuration:</p>
                  <p className="text-amber-700 dark:text-amber-300 mb-2">
                    In your Facebook App → Webhooks, use this callback URL:
                  </p>
                  <code className="block p-2 bg-amber-100 dark:bg-amber-900 rounded text-xs break-all">
                    {process.env.REACT_APP_BACKEND_URL}/webhook/facebook
                  </code>
                  <p className="text-amber-700 dark:text-amber-300 mt-2">
                    Verify Token: <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">unicrm_webhook_verify</code>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFbDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveFacebookIntegration} disabled={savingFb} data-testid="save-fb-btn">
              {savingFb ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {integrations.facebook ? 'Update' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
